import React, { useEffect, useMemo, useRef } from 'react';
import { 
    ActionButton, 
    CheckboxVisibility, 
    DetailsList, 
    DetailsListLayoutMode, 
    DetailsRow, 
    IButtonStyles, 
    IColumn, 
    IDetailsRowStyles, 
    SelectionMode,
    Selection,
    ISelection,
    IObjectWithKey,
    IDetailsList,
} from '@fluentui/react';
import WarningMessageComponent from '../warning-message/warning-message.component';

interface ITableDefaultComponentProps {
    items: any[];
    selectedItem?: any;
    columns?: IColumn[];
    noRowsMessage?: string;
    onRowClick?: (item: any, index: number) => void;
    onRowDoubleClick?: (item: any, index: number) => void;
};

const TableDefaultComponent = (props: ITableDefaultComponentProps) => {
    const detailsListRef = useRef<IDetailsList>(null);

    const _onRenderRow = (row?: { itemIndex: number, item: any }) => {
        if (row) {
            const styles: Partial<IDetailsRowStyles> = {
                root: {
                    selectors: {
                        ".ms-DetailsRow-cell": {
                            lineHeight: 42,
                            padding: 0,
                        },
                        "button": {
                            paddingTop: 10,
                            paddingBottom: 10,
                            height: '100%',
                        },
                        "&:hover": {
                            backgroundColor: "var(--color-primario)",
                            selectors: {
                                "button": {
                                    color: 'white',
                                },
                                ".ms-DetailsRow-cell": {
                                    color: 'white',
                                },
                            },
                        },
                        "&.is-selected": {
                            backgroundColor: "var(--color-primario)",
                            selectors: {
                                "button": {
                                    color: 'white',
                                },
                                ".ms-DetailsRow-cell": {
                                    color: 'white',
                                },
                            },
                        },
                    },
                },
            };

            if (row.itemIndex % 2 === 0) {
                styles.root = { 
                    backgroundColor: '#f2f2f2',
                    selectors: {
                        ".ms-DetailsRow-cell": {
                            lineHeight: 42,
                            padding: 0,
                        },
                        "button": {
                            paddingTop: 10,
                            paddingBottom: 10,
                            height: '100%',
                        },
                        "&:hover": {
                            backgroundColor: "var(--color-primario)",
                            selectors: {
                                "button": {
                                    color: 'white',
                                },
                                ".ms-DetailsRow-cell": {
                                    color: 'white',
                                },
                            },
                        },
                        "&.is-selected": {
                            backgroundColor: "var(--color-primario)",
                            selectors: {
                                "button": {
                                    color: 'white',
                                },
                                ".ms-DetailsRow-cell": {
                                    color: 'white',
                                },
                            },
                        },
                    },
                };
            }
            
            return (
                <DetailsRow 
                    {...row} 
                    styles={styles}
                    data-selection-disabled={true} />
            );
        }
        else {
            return null;
        }
    };

    const onRenderItemColumn = (item?: any, index?: number, column?: IColumn) : React.ReactNode => {
        if (item && index !== undefined && column && column.fieldName) {
            const styles : IButtonStyles = {
                root: {
                    backgroundColor: 'transparent',
                    color: '#666666',
                    height: 'auto',
                    display: 'block',
                    width: '100%',
                },
                rootHovered: {
                    backgroundColor: 'transparent',
                    color: 'white',
                },
            };

            const onActionButtonClick = () => {
                if (props.onRowClick) {
                    props.onRowClick(item, index);
                }
            };

            const onActionButtonDoubleClick = () => {
                if (props.onRowDoubleClick) {
                    props.onRowDoubleClick(item, index);
                }
            };

            return (
                <ActionButton 
                    onClick={onActionButtonClick}
                    onDoubleClick={onActionButtonDoubleClick}
                    styles={styles}>
                    {item[column.fieldName]}
                </ActionButton>
            );
        }
        else {
            return null;
        }
    };

    const selection : ISelection<IObjectWithKey> = useMemo<Selection>(() => {
        return new Selection();
    }, []);

    useEffect(() => {
        if (props.selectedItem) {
            selection.setAllSelected(false);
            const index : number = props.items.indexOf(props.selectedItem);
            detailsListRef.current?.focusIndex(index);
            selection.setIndexSelected(index, true, false);
        }
    }, [props.selectedItem, props.items, selection]);

    return (
        !props.items.length ? (
            props.noRowsMessage ? (
                <WarningMessageComponent message={props.noRowsMessage} />
            ) : (
                null
            )
        ) : (
            <div>
                <DetailsList
                    componentRef={detailsListRef}
                    selectionMode={SelectionMode.single}
                    selection={selection}
                    checkboxVisibility={CheckboxVisibility.hidden}
                    layoutMode={DetailsListLayoutMode.justified}
                    onRenderRow={_onRenderRow}
                    items={props.items}
                    columns={props.columns}
                    onRenderItemColumn={onRenderItemColumn}
                    data-is-scrollable="true" />
            </div>
        )
    );
};

export default TableDefaultComponent;
